"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { postQuestion } from "@/api/auth";
import { ErrorNotif } from "@/components/errorNotif/error";
import { useState } from "react";
import PersianLetterInput from "@/components/persian-letter-input";
import SimpleTextField from "@/components/simple-text-field";
import {
  TicketFormValues,
  ticketSchema,
} from "@/components/schema/contact-us";
import NumberInput from "@/components/number-input";
import { useAuthStore } from "@/store/authStore";
import SuccessModal from "@/components/success-modal";
import { Button } from "@/components/button";

export function QuestionForm() {
  const [errorMessage, setErrorMessage] = useState("");
  const [isVisibleSuccessModal, setIsVisibleSuccessModal] = useState<boolean>(false);
  const { isLoggedIn } = useAuthStore.getState();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TicketFormValues>({
    resolver: zodResolver(ticketSchema),
    reValidateMode: "onBlur",
    mode: "onBlur",
    defaultValues: {
      first_name: "",
      last_name: "",
      mobile: "",
      email: "",
      subject: "",
      body: "",
    },
  });

  const onSubmit: SubmitHandler<TicketFormValues> = async (data) => {
    try {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (isLoggedIn && ["first_name", "last_name", "mobile"].includes(key)) {
          return;
        }

        if (value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      await postQuestion(formData);
      setIsVisibleSuccessModal(true);
      reset();
      setErrorMessage("")
    } catch (e: any) {
      console.error(e?.response?.data?.message || "خطا در ارسال اطلاعات");
      setErrorMessage(e?.response?.data?.message || "خطایی رخ داد");
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-2 p-3"
    >
      <p id="website-form" className="font-bold text-right text-lg text-gray-800 mb-2">
        پیام خود را برای ما ارسال کنید.
      </p>

      <SuccessModal
        open={isVisibleSuccessModal}
        onClose={() => setIsVisibleSuccessModal(false)}
        description="پیام شما با موفقیت ثبت شد."
      />

      <PersianLetterInput
        {...register("first_name")}
        label="نام"
        placeholder="مثال: رضا"
        requiredMark
        maxLength={50}
        errors={errors.first_name}
      />

      <PersianLetterInput
        {...register("last_name")}
        label="نام خانوادگی"
        placeholder="مثال: علوی"
        requiredMark
        maxLength={50}
        errors={errors.last_name}
      />

      <NumberInput
        {...register("mobile")}
        label="شماره تلفن همراه"
        placeholder="مثال: ۰۹۰۱۲۳۴۵۶۷۸"
        requiredMark
        minLength={11}
        maxLength={11}
        errors={errors.mobile}
      />

      <SimpleTextField
        {...register("email")}
        label="ایمیل"
        type="email"
        placeholder="ایمیل خود را وارد کنید"
        autoComplete="off"
        errors={errors.email}
      />

      <SimpleTextField
        {...register("subject")}
        label="موضوع"
        placeholder="موضوع پیام را وارد کنید"
        errors={errors.subject}
        maxLength={200}
        requiredMark
      />

      <SimpleTextField
        {...register("body")}
        label="متن پیام"
        placeholder="متن پیام خود را وارد کنید"
        errors={errors.body}
        maxLength={2000}
        rows={5}
        as="textarea"
        requiredMark
      />
      {errorMessage && (
        <div className=" my-2">
          <ErrorNotif description={errorMessage} />
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
        <Button
          variant="bare"
          type="submit"
          text={isSubmitting ? "در حال ارسال..." : " ارسال پیام"}
          disabled={isSubmitting}
          className="h-11 bg-Accent hover:bg-Accent-700 text-gray-600 font-medium rounded-3xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        />
      </div>
    </form>
  );
}
